import commonResource from '../../locales/th/breadcrumb.json';
import {
  getMainCategory,
  PostMainCategory,
  PostSubcategory,
} from '../../common/helpers/domain/postCategories';
import { PropertyType } from '../../common/helpers/domain/propertyTypes';
import { BreadcrumbItem } from '../global/breadcrumb/Breadcrumb';
import {
  generatePostMainCategoryLandingPageUrl,
  generatePostSubCategoryLandingPageUrl,
} from '../../common/helpers/routing/landingPageUrlGenerator';
import {
  LandingPageTypes,
  LocationInsightSubLandingPageType,
} from '../../common/helpers/domain/landingPages';

const breadcrumbCategoryTextResourceMap = new Map<
  PostMainCategory | PostSubcategory,
  string
>([
  [
    PostMainCategory.ProjectResearch,
    commonResource.breadcrumb.projectResearch.main,
  ],
  [
    PostMainCategory.LocationInsight,
    commonResource.breadcrumb.locationInsight.main,
  ],
  [PostMainCategory.News, commonResource.breadcrumb.news.main],
  [PostMainCategory.Guides, commonResource.breadcrumb.guides.main],
  [PostMainCategory.Decor, commonResource.breadcrumb.decor.main],
  [PostSubcategory.Review, commonResource.breadcrumb.projectResearch.review],
  [PostSubcategory.Preview, commonResource.breadcrumb.projectResearch.preview],
  [PostSubcategory.PointOfView, commonResource.breadcrumb.projectResearch.pov],
  [PostSubcategory.BuyAndSell, commonResource.breadcrumb.guides.buyAndSell],
  [PostSubcategory.Rent, commonResource.breadcrumb.guides.rent],
  [PostSubcategory.Investment, commonResource.breadcrumb.guides.investment],
  [PostSubcategory.Finance, commonResource.breadcrumb.guides.finance],
  [PostSubcategory.PropertyNews, commonResource.breadcrumb.news.propertyNews],
  [PostSubcategory.GeneralNews, commonResource.breadcrumb.news.generalNews],
  [
    PostSubcategory.InteriorInspiration,
    commonResource.breadcrumb.decor.interiorInspiration,
  ],
  [PostSubcategory.Renovation, commonResource.breadcrumb.decor.renovation],
  [PostSubcategory.Maintenance, commonResource.breadcrumb.decor.maintenance],
]);

export const getBreadcrumbPostCategoryText = (
  postCategory: PostMainCategory | PostSubcategory,
): string => breadcrumbCategoryTextResourceMap.get(postCategory) ?? '';

const breadcrumbPropertyTypeThaiResourceMap = new Map<PropertyType, string>([
  [PropertyType.Condominium, commonResource.breadcrumb.properties.condo],
  [PropertyType.House, commonResource.breadcrumb.properties.house],
  [PropertyType.Townhome, commonResource.breadcrumb.properties.townhome],
  [
    PropertyType.HolidayHomes,
    commonResource.breadcrumb.properties.holidayHomes,
  ],
  [PropertyType.HomeOffice, commonResource.breadcrumb.properties.homeOffice],
  [PropertyType.MixedUse, commonResource.breadcrumb.properties.mixedUse],
  [PropertyType.Commercial, commonResource.breadcrumb.properties.commercial],
]);

export const getBreadcrumbPropertyTypeInThai = (
  propertyType: PropertyType,
): string | undefined =>
  breadcrumbPropertyTypeThaiResourceMap.get(propertyType);

const homeItem = {
  text: 'Home',
  url: '/',
};

export const generatePostMainCategoryBreadcrumbList = (
  postMainCategory: PostMainCategory,
): BreadcrumbItem[] => {
  return [
    homeItem,
    {
      text: getBreadcrumbPostCategoryText(postMainCategory),
      url: undefined,
    },
  ];
};

export const generateLocationInsightSubLandingPageBreadcrumbList = (
  landingPageType: LocationInsightSubLandingPageType,
): BreadcrumbItem[] => {
  return [
    homeItem,
    {
      text: getBreadcrumbPostCategoryText(PostMainCategory.LocationInsight),
      url: generatePostMainCategoryLandingPageUrl(
        PostMainCategory.LocationInsight,
      ),
    },
    {
      text:
        landingPageType === LandingPageTypes.ZonesPage
          ? commonResource.breadcrumb.locationInsight.zone
          : commonResource.breadcrumb.locationInsight.trainLine,
      url: undefined,
    },
  ];
};

export const generatePostSubcategoryBreadcrumbList = (
  postSubcategory: PostSubcategory,
): BreadcrumbItem[] => {
  return [
    homeItem,
    {
      text: getBreadcrumbPostCategoryText(getMainCategory(postSubcategory)),
      url: generatePostMainCategoryLandingPageUrl(
        getMainCategory(postSubcategory),
      ),
    },
    {
      text: getBreadcrumbPostCategoryText(postSubcategory),
      url: undefined,
    },
  ];
};

export const generatePropertyTypeLandingPageBreadcrumbList = (
  propertyType: PropertyType,
): BreadcrumbItem[] => {
  return [
    homeItem,
    {
      text: getBreadcrumbPropertyTypeInThai(propertyType),
      url: undefined,
    },
  ];
};

export const generateComboLandingPageBreadcrumbList = (
  postSubcategory: PostSubcategory,
  propertyType: PropertyType,
): BreadcrumbItem[] => {
  return [
    homeItem,
    {
      text: getBreadcrumbPostCategoryText(getMainCategory(postSubcategory)),
      url: generatePostMainCategoryLandingPageUrl(
        getMainCategory(postSubcategory),
      ),
    },
    {
      text: getBreadcrumbPostCategoryText(postSubcategory),
      url: generatePostSubCategoryLandingPageUrl(postSubcategory),
    },
    {
      text: getBreadcrumbPropertyTypeInThai(propertyType),
      url: undefined,
    },
  ];
};
